import { Injectable, inject } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { Observable, of } from 'rxjs';

import { ApiService } from './api.service';
import { HttpParams } from '@angular/common/http';

export interface Subscription {
  id: string;
  productTierId: string;
  stripeId: string;
  name: string;
  description: string;
  display: string;
  term: number;
  price: number;
}

export interface BillingAccount {
  customerId: string;
  subscriptionId: string;
  clientSecret: string;
}

export interface UpgradeBillingAccount {
  subscriptionId: string;
  clientSecret: string;
}

export interface AddAccount {
  organizationIid: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  title: string;
  promoCode?: string;
  subscriptionId: string;
  billingCustomerId?: string;
  billingSubscriptionId?: string;
  planType?: string;
}

export interface UpgradeAccount {
  organizationId: string;
  subscriptionId: string;
  billingCustomerId?: string;
  billingSubscriptionId?: string;
  planType: string;
  email: string;
  name: string;
}

interface InstitutionCheckResult {
  institutionExists: boolean;
  hasNonMaturity: boolean;
  hasPeerComparison: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private apiService = inject(ApiService);

  checkEmail(email: string): Observable<ValidationErrors | null> {
    return this.apiService.get<ValidationErrors | null>(`api/account/check/email?value=${encodeURIComponent(email)}`);
  }

  checkInstitution(iid: number): Observable<InstitutionCheckResult | null> {
    return this.apiService.get<InstitutionCheckResult | null>(`api/account/check/institution?value=${iid}`);
  }

  checkPromoCode(code: string): Observable<ValidationErrors | null> {
    if (code != null && code.length > 0) {
      return this.apiService.get(`api/account/check/promo?value=${code}`);
    } else {
      return of(null);
    }
  }

  getSubscriptions(planCode: string): Observable<Subscription[]> {
    return this.apiService.get<Subscription[]>(`api/account/subscription/${planCode}`);
  }

  addBillingAccount(email: string, name: string, subscription: string, paymentMethodId: string): Observable<BillingAccount> {
    return this.apiService.post<BillingAccount>('api/account/billing', { email: email, name: name, subscription: subscription, paymentMethodId: paymentMethodId })
  }

  upgradeBillingAccount(email: string, name: string, organizationId: string, stripePriceId: string, paymentMethodId: string, clientClaim: string): Observable<UpgradeBillingAccount> {
    return this.apiService.post<UpgradeBillingAccount>('api/account/billing/upgrade',
      { email: email, name: name, organizationId: organizationId, stripePriceId: stripePriceId, paymentMethodId: paymentMethodId, clientClaim })
  }

  addAccount(model: AddAccount): Observable<void> {
    return this.apiService.post<void>('api/account', model)
  }

  upgradeAccount(model: UpgradeAccount): Observable<void> {
    return this.apiService.post<void>('api/account/upgrade', model)
  }

  addUserAccountToExitingOrganization(model: AddAccount): Observable<void> {
    return this.apiService.post<void>('api/account/existing', model)
  }

  cancelSubscription(organizationId: string, productTierId: string, stripeId?: string): Observable<void> {
    const queryParams: { [key: string]: string | undefined } = {};
    if (stripeId) {
      queryParams['stripeId'] = stripeId;
    }

    return this.apiService.postWithQuery<void>(`api/account/cancel/${organizationId}/${productTierId}`, {}, queryParams);
  }

  ReactivateSubscription(organizationId: string, productTierId: string, stripeId?: string): Observable<void> {
    const queryParams: { [key: string]: string | undefined } = {};
    if (stripeId) {
      queryParams['stripeId'] = stripeId;
    }

    return this.apiService.postWithQuery<void>(`api/account/activate/${organizationId}/${productTierId}`, {}, queryParams);
  }

}
